/**
 * Vehicle document validation — tarjeta de circulación.
 */
import { z } from 'zod'

function requiresLiveProvenance(uri: string | undefined): boolean {
  if (!uri || uri === 'existing') return false
  return !/^https?:\/\//i.test(uri)
}

export const vehicleDocumentSchema = z
  .object({
    documentNumber: z.string().max(30).optional().default(''),
    expiryDate: z
      .string()
      .optional()
      .default('')
      .refine((d) => !d || new Date(d) > new Date(), 'listing.tarjeta_expired_error'),
    ownerName: z.string().optional().default(''),
    frontPhotoUri: z.string().min(1, 'listing.tarjeta_required'),
    frontPhotoCapturedAt: z.string().optional(),
    frontPhotoCaptureSource: z.literal('tago_live_camera').optional(),
    backPhotoUri: z.string().min(1, 'listing.tarjeta_back_required'),
    backPhotoCapturedAt: z.string().optional(),
    backPhotoCaptureSource: z.literal('tago_live_camera').optional(),
    tarjetaAiVerified: z.boolean().optional().default(false),
    tarjetaBackAiVerified: z.boolean().optional().default(false),
    // Policy can be gathered later. Tarjeta uploads must still be persisted as a
    // recoverable draft; publication remains blocked by the DB completeness gate.
    insurancePolicyPhotoUri: z.string().optional().default(''),
    insurancePolicyPhotoCapturedAt: z.string().optional(),
    insurancePolicyPhotoCaptureSource: z.literal('tago_live_camera').optional(),
    insurancePolicyNumber: z.string().max(60).optional().default(''),
    insurancePolicyExpiryDate: z
      .string()
      .optional()
      .default('')
      .refine((d) => !d || new Date(d) > new Date(), 'listing.policy_expired_error'),
    insuranceInsurerName: z.string().max(120).optional().default(''),
    insurancePolicyAiVerified: z.boolean().optional().default(false),
  })
  .superRefine((data, ctx) => {
    if (requiresLiveProvenance(data.frontPhotoUri)) {
      if (!data.frontPhotoCapturedAt) {
        ctx.addIssue({
          code: 'custom',
          path: ['frontPhotoCapturedAt'],
          message: 'listing.document_live_capture_required',
        })
      }
      if (data.frontPhotoCaptureSource !== 'tago_live_camera') {
        ctx.addIssue({
          code: 'custom',
          path: ['frontPhotoCaptureSource'],
          message: 'listing.document_live_capture_required',
        })
      }
    }

    if (requiresLiveProvenance(data.backPhotoUri)) {
      if (!data.backPhotoCapturedAt) {
        ctx.addIssue({
          code: 'custom',
          path: ['backPhotoCapturedAt'],
          message: 'listing.document_live_capture_required',
        })
      }
      if (data.backPhotoCaptureSource !== 'tago_live_camera') {
        ctx.addIssue({
          code: 'custom',
          path: ['backPhotoCaptureSource'],
          message: 'listing.document_live_capture_required',
        })
      }
    }

    if (requiresLiveProvenance(data.insurancePolicyPhotoUri)) {
      if (!data.insurancePolicyPhotoCapturedAt) {
        ctx.addIssue({
          code: 'custom',
          path: ['insurancePolicyPhotoCapturedAt'],
          message: 'listing.document_live_capture_required',
        })
      }
      if (data.insurancePolicyPhotoCaptureSource !== 'tago_live_camera') {
        ctx.addIssue({
          code: 'custom',
          path: ['insurancePolicyPhotoCaptureSource'],
          message: 'listing.document_live_capture_required',
        })
      }
    }
  })
